/**
 * Client-side money handling.
 *
 * The browser never computes tax — that comes from the server's engine. What
 * it does do is parse what a user types into minor units and render minor
 * units back out, and those two must agree exactly with the server.
 */

const MINOR_UNIT_EXPONENTS: Readonly<Record<string, number>> = {
  BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
  JPY: 0, KRW: 0, VND: 0, CLP: 0, ISK: 0,
};

const CURRENCY_SYMBOLS: Readonly<Record<string, string>> = {
  INR: '₹', AED: 'AED ', USD: '$', EUR: '€', GBP: '£',
  SAR: 'SAR ', SGD: 'S$', AUD: 'A$', CAD: 'C$', JPY: '¥',
};

export const QTY_SCALE = 1000;

export function minorUnitExponent(currency: string): number {
  return MINOR_UNIT_EXPONENTS[currency.toUpperCase()] ?? 2;
}

export function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency.toUpperCase()] ?? `${currency.toUpperCase()} `;
}

function roundHalfAwayFromZero(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/** Parse "1,234.50" into minor units. Returns 0 for blank, null for invalid. */
export function parseAmount(input: string, currency = 'INR'): number | null {
  const text = input.replace(/[,\s\u00A0\u202F]/g, '').trim();
  if (text === '' || text === '-') return 0;
  if (!/^-?\d*(\.\d*)?$/.test(text)) return null;

  const exponent = minorUnitExponent(currency);
  const negative = text.startsWith('-');
  const [whole = '0', fraction = ''] = text.replace('-', '').split('.');
  const padded = (fraction + '0'.repeat(exponent + 1)).slice(0, exponent + 1);
  const base = Number(whole) * 10 ** exponent + Number(padded.slice(0, exponent) || '0');
  const carry = Number(padded[exponent] ?? '0') >= 5 ? 1 : 0;
  const value = base + carry;
  return negative ? -value : value;
}

export function parseQuantity(input: string): number | null {
  const text = input.replace(/[,\s\u00A0\u202F]/g, '').trim();
  if (text === '') return 0;
  if (!/^-?\d*(\.\d*)?$/.test(text)) return null;
  return roundHalfAwayFromZero(Number(text) * QTY_SCALE);
}

export function parseRate(input: string): number | null {
  const text = input.replace(/[%,\s\u00A0\u202F]/g, '').trim();
  if (text === '') return 0;
  if (!/^-?\d*(\.\d*)?$/.test(text)) return null;
  return roundHalfAwayFromZero(Number(text) * 100);
}

/** Plain decimal, no symbol or grouping — for use inside form inputs. */
export function toInputValue(amountMinor: number, currency = 'INR'): string {
  const exponent = minorUnitExponent(currency);
  const negative = amountMinor < 0;
  const digits = String(Math.abs(amountMinor)).padStart(exponent + 1, '0');
  const whole = digits.slice(0, digits.length - exponent) || '0';
  const fraction = exponent === 0 ? '' : `.${digits.slice(digits.length - exponent)}`;
  return `${negative ? '-' : ''}${whole}${fraction}`;
}

/**
 * India groups the integer part 2,2,3 from the right, not in thousands.
 * Getting this wrong is instantly noticeable to an Indian reader.
 */
function groupDigits(whole: string, currency: string): string {
  if (currency.toUpperCase() !== 'INR') {
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  if (whole.length <= 3) return whole;
  const last3 = whole.slice(-3);
  return `${whole.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`;
}

export function formatMoney(amountMinor: number, currency = 'INR', withSymbol = true): string {
  const plain = toInputValue(amountMinor, currency);
  const negative = plain.startsWith('-');
  const [whole = '0', fraction] = (negative ? plain.slice(1) : plain).split('.');
  const grouped = groupDigits(whole, currency);
  const body = fraction ? `${grouped}.${fraction}` : grouped;
  return `${negative ? '−' : ''}${withSymbol ? currencySymbol(currency) : ''}${body}`;
}

/** Drop the decimals for headline figures where the paise are noise. */
export function formatMoneyCompact(amountMinor: number, currency = 'INR'): string {
  const exponent = minorUnitExponent(currency);
  const factor = 10 ** exponent;
  const major = Math.trunc(Math.abs(amountMinor) / factor);
  const sign = amountMinor < 0 ? '−' : '';
  return `${sign}${currencySymbol(currency)}${groupDigits(String(major), currency)}`;
}

export function formatQuantity(quantityMilli: number): string {
  const value = quantityMilli / QTY_SCALE;
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(3)));
}

export function toQuantityInput(quantityMilli: number): string {
  return formatQuantity(quantityMilli);
}

export function formatRate(rateBp: number): string {
  const value = rateBp / 100;
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
}

/* -------------------------------------------------------------------------- */
/* Dates                                                                       */
/* -------------------------------------------------------------------------- */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The user's local date, which is what "today" means on an invoice. */
export function today(): string {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

export function addDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00`);
  date.setDate(date.getDate() + days);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

/** Parsed by hand so a date never shifts a day because of a timezone. */
export function formatDate(isoDate: string | null | undefined): string {
  if (!isoDate) return '—';
  const [year, month, day] = isoDate.slice(0, 10).split('-');
  const monthName = MONTHS[Number(month) - 1];
  if (!year || !monthName || !day) return isoDate;
  return `${Number(day)} ${monthName} ${year}`;
}

/**
 * A timestamp, shown in the reader's own zone.
 *
 * Unlike formatDate this is a real instant rather than a calendar day, so
 * converting it to local time is correct — "sent at 9:04pm" should mean 9:04pm
 * where you are reading it.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;

  // Both halves must come from the same clock. Taking the date from the UTC
  // string and the time from the local one made anything sent after 18:30 IST
  // display yesterday's date beside today's time.
  const day = `${at.getDate()} ${MONTHS[at.getMonth()]} ${at.getFullYear()}`;
  const time = at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day}, ${time}`;
}

export function formatMonth(yearMonth: string): string {
  const [year, month] = yearMonth.split('-');
  const monthName = MONTHS[Number(month) - 1];
  return monthName ? `${monthName} ${String(year).slice(2)}` : yearMonth;
}

/** "in 12 days", "3 days ago", "today" — relative to the user's local date. */
export function relativeDays(isoDate: string | null | undefined): string {
  if (!isoDate) return '';
  const target = Date.parse(`${isoDate}T00:00:00`);
  const now = Date.parse(`${today()}T00:00:00`);
  if (Number.isNaN(target)) return '';
  const days = Math.round((target - now) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days overdue`;
}

/**
 * A plan price after a negotiated discount.
 *
 * Mirrors `discountedPrice` in the server's money domain, deliberately down to
 * the rounding: this drives a live preview while an operator types a
 * percentage, and a preview that disagrees with the price the server goes on
 * to store is worse than no preview at all. The paired tests assert the same
 * values on both sides, so the two cannot drift quietly.
 */
export function discountedPrice(priceMinor: number, discountBp: number): number {
  if (discountBp <= 0) return priceMinor;
  if (discountBp >= 10_000) return 0;
  return priceMinor - roundHalfAwayFromZero((priceMinor * discountBp) / 10_000);
}
